import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Package, Truck, FileDown, FileText, Printer } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useActiveTenant } from '../contexts/tenant'

type WaveOrder = { orderId: string; groupKey: string; orderNumber: string; carrier: string | null; customerName: string | null; status: string }
type Group = { key: string; count: number; orders: WaveOrder[] }
type SkuGuide = { sku: string; name: string; units: number; orders: number }
type Carrier = { carrier: string; orders: number }
type WaveDetail = {
  wave: { id: string; number: string; waveDate: string; status: string; createdAt: string; closedAt: string | null }
  totalOrders: number
  groups: Group[]
  skuGuide: SkuGuide[]
  byCarrier: Carrier[]
}

const statusLabel: Record<string, string> = {
  open: 'Aberta', picking: 'Em separação', checked: 'Conferida', closed: 'Fechada', cancelled: 'Cancelada',
}
const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  open: 'default', picking: 'info', checked: 'warning', closed: 'success', cancelled: 'danger',
}

export function PickingWaveDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const tenantId = useActiveTenant()

  const { data, isLoading } = useQuery<WaveDetail>({
    queryKey: ['picking-wave', id],
    queryFn: async () => (await api.get(`/picking/waves/${id}`)).data,
    enabled: !!id,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['picking-wave', id] })
    qc.invalidateQueries({ queryKey: ['picking-waves', tenantId] })
  }

  const pack = useMutation({
    mutationFn: async () => (await api.post(`/expedition/waves/${id}/pack`, { tenantId })).data,
    onSuccess: invalidate,
  })
  const ship = useMutation({
    mutationFn: async () => (await api.post(`/expedition/waves/${id}/ship`, { tenantId })).data,
    onSuccess: invalidate,
  })

  const [busy, setBusy] = useState<'danfes' | 'labels' | 'packet' | null>(null)
  const [waveMsg, setWaveMsg] = useState<string | null>(null)

  async function downloadReport(number: string) {
    const res = await api.get(`/picking/waves/${id}/report`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `folha-${number}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  async function downloadDanfes(number: string) {
    setBusy('danfes'); setWaveMsg(null)
    try {
      const res = await api.get(`/picking/waves/${id}/danfes`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url; a.download = `danfes-${number}.pdf`; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      const skipped = Number(res.headers['x-danfe-skipped'] ?? 0)
      const count = Number(res.headers['x-danfe-count'] ?? 0)
      setWaveMsg(`${count} DANFE(s) geradas${skipped ? ` · ${skipped} pedido(s) sem NF ficaram de fora` : ''}.`)
    } catch (e: any) {
      let msg = 'Falha ao gerar as DANFEs'
      try { msg = JSON.parse(await e?.response?.data?.text?.() ?? '{}').title ?? msg } catch { /* ignore */ }
      setWaveMsg(msg)
    } finally { setBusy(null) }
  }

  async function baixarExpedicao(number: string) {
    setBusy('packet'); setWaveMsg(null)
    try {
      const res = await api.get(`/picking/waves/${id}/packet`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url; a.download = `expedicao-${number}.pdf`; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      const skipped = Number(res.headers['x-packet-skipped'] ?? 0)
      const count = Number(res.headers['x-packet-count'] ?? 0)
      setWaveMsg(`Arquivo de expedição: ${count} pedido(s) com etiqueta + DANFE${skipped ? ` · ${skipped} ficaram de fora` : ''}.`)
    } catch (e: any) {
      let msg = 'Falha ao gerar o arquivo de expedição'
      try { msg = JSON.parse(await e?.response?.data?.text?.() ?? '{}').title ?? msg } catch { /* ignore */ }
      setWaveMsg(msg)
    } finally { setBusy(null) }
  }

  async function abrirEtiquetas() {
    setBusy('labels'); setWaveMsg(null)
    try {
      const { data: r } = await api.post(`/picking/waves/${id}/labels`)
      window.open(r.url, '_blank')
      setWaveMsg(`${r.count} etiqueta(s) enviadas para impressão no Melhor Envio.`)
    } catch (e: any) {
      setWaveMsg(e?.response?.data?.title ?? 'Falha ao gerar as etiquetas')
    } finally { setBusy(null) }
  }

  if (isLoading) return <p className="p-8 text-center text-sm text-gray-400">Carregando...</p>
  if (!data) return <p className="p-8 text-center text-sm text-gray-400">Onda não encontrada.</p>

  const { wave, totalOrders, groups, skuGuide, byCarrier } = data
  const totalUnits = skuGuide.reduce((s, g) => s + g.units, 0)

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/picking')}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h2 className="text-xl font-mono font-bold text-gray-900">{wave.number}</h2>
          <p className="text-sm text-gray-500">{new Date(wave.waveDate).toLocaleDateString('pt-BR')} · {totalOrders} pedidos</p>
        </div>
        <Badge variant={statusVariant[wave.status] ?? 'default'}>{statusLabel[wave.status] ?? wave.status}</Badge>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => downloadReport(wave.number)}>
            <FileDown className="h-4 w-4" /> Baixar folha
          </Button>
          <Button size="sm" variant="secondary" loading={busy === 'labels'} onClick={() => abrirEtiquetas()}>
            <Printer className="h-4 w-4" /> Etiquetas
          </Button>
          <Button size="sm" variant="secondary" loading={busy === 'danfes'} onClick={() => downloadDanfes(wave.number)}>
            <FileText className="h-4 w-4" /> DANFEs
          </Button>
          <Button size="sm" loading={busy === 'packet'} onClick={() => baixarExpedicao(wave.number)}>
            <FileDown className="h-4 w-4" /> Expedição (etiq+NF)
          </Button>
          <Button size="sm" variant="secondary" loading={pack.isPending} onClick={() => pack.mutate()}>
            <Package className="h-4 w-4" /> Embalar onda
          </Button>
          <Button size="sm" loading={ship.isPending} onClick={() => ship.mutate()}>
            <Truck className="h-4 w-4" /> Expedir onda
          </Button>
        </div>
      </div>

      {(pack.data || ship.data) && (
        <p className="text-sm text-gray-600">
          {pack.data ? `${(pack.data as any).packed} pedido(s) embalado(s). ` : ''}
          {ship.data ? `${(ship.data as any).shipped} pedido(s) expedido(s).` : ''}
        </p>
      )}

      {waveMsg && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">{waveMsg}</div>
      )}

      {/* Guia consolidada por SKU (apanhado da mesa) */}
      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Guia de separação (total por SKU)</span>
          <span className="text-xs text-gray-400">{totalUnits} unidades</span>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                <th className="px-5 py-3 text-right font-medium text-gray-600">Unidades</th>
                <th className="px-5 py-3 text-right font-medium text-gray-600">Pedidos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {skuGuide.map(s => (
                <tr key={s.sku} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-mono text-gray-700">{s.sku}</td>
                  <td className="px-5 py-3 text-gray-900">{s.name}</td>
                  <td className="px-5 py-3 text-right font-bold">{s.units}</td>
                  <td className="px-5 py-3 text-right text-gray-500">{s.orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Grupos de pedidos idênticos */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">Grupos de pedidos idênticos ({groups.length})</h3>
        {groups.map((g, idx) => (
          <Card key={g.key}>
            <CardHeader>
              <span className="text-sm font-semibold text-gray-900">Grupo {idx + 1}</span>
              <Badge variant="info">{g.count} pedidos</Badge>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Pedido</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Transportadora</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {g.orders.map(o => (
                    <tr key={o.orderId} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">{o.orderNumber}</td>
                      <td className="px-5 py-3 text-gray-600">{o.customerName ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-600">{o.carrier ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-500">{o.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Resumo por transportadora */}
      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Resumo por transportadora</span></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-gray-600">Transportadora</th>
                <th className="px-5 py-3 text-right font-medium text-gray-600">Pedidos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {byCarrier.map(c => (
                <tr key={c.carrier} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-700">{c.carrier}</td>
                  <td className="px-5 py-3 text-right font-semibold">{c.orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}
